/// <amd-module name="VSSUI/Components/TeachingBubble/TeachingBubble" />
import * as React from "react";

import { CustomTeachingBubble } from "./CustomTeachingBubble";
import { ITeachingBubbleProps, ITeachingBubble } from "./TeachingBubble.Props";

import { IButtonProps } from "VSSUI/Button";
import { Image } from "VSSUI/Image";
import { Header, TitleSize } from "VSSUI/Header";
import { PanelCloseButton, PanelFooter } from "VSSUI/Panel";
import { Spacing, Surface, SurfaceBackground } from "VSSUI/Surface";
import { css } from "VSSUI/Util";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";

let bubbleId = 1;

export class TeachingBubble extends React.Component<ITeachingBubbleProps> implements ITeachingBubble {
    private readonly customBubble = React.createRef<CustomTeachingBubble>();
    private bubbleId = `bubble-${bubbleId++}`;

    public render() {
        let defaultActiveElement = this.props.defaultActiveElement;

        if (!defaultActiveElement && !this.props.textOnly) {
            if (this.props.primaryButtonProps) {
                defaultActiveElement = ".bolt-bubble-primary-button";
            } else {
                defaultActiveElement = ".bolt-bubble-close";
            }
        }

        return (
            <CustomTeachingBubble
                {...this.props}
                ariaDescribedBy={this.props.ariaDescribedBy || `${this.bubbleId}-description`}
                ariaLabeledBy={this.props.ariaLabeledBy || `${this.bubbleId}-title`}
                defaultActiveElement={defaultActiveElement}
                onLocationChange={this.setFocus}
                ref={this.customBubble}
            >
                {this.getContent()}
            </CustomTeachingBubble>
        );
    }

    private getContent = () => {
        const { children, imageProps, primaryButtonProps, text, textOnly, title } = this.props;

        if (textOnly) {
            return <div className="bolt-bubble-text-only body-m">{text}</div>;
        }

        return (
            <Surface background={SurfaceBackground.callout} spacing={Spacing.condensed}>
                <div className="flex-row justify-center">{imageProps && <Image {...imageProps} />}</div>
                <PanelCloseButton className="bolt-bubble-close" onDismiss={this.dismiss} />
                <Header
                    className={css("bolt-bubble-text", !primaryButtonProps && "no-buttons")}
                    description={text}
                    descriptionClassName="bolt-bubble-description"
                    descriptionId={`${this.bubbleId}-description`}
                    title={title}
                    titleSize={TitleSize.Small}
                    titleId={`${this.bubbleId}-title`}
                />
                {children}
                {primaryButtonProps && (
                    <FocusZone direction={FocusZoneDirection.Horizontal}>
                        <div className="bolt-bubble-buttons">
                            <PanelFooter buttonProps={this.getButtons()} />
                        </div>
                    </FocusZone>
                )}
            </Surface>
        );
    };

    private getButtons = (): IButtonProps[] => {
        const { primaryButtonProps, secondaryButtonProps } = this.props;

        const buttons: IButtonProps[] = [{ ...primaryButtonProps, className: css("bolt-bubble-primary-button", primaryButtonProps!.className) }];
        secondaryButtonProps && buttons.push(secondaryButtonProps);

        return buttons;
    };

    private setFocus = () => {
        const { onLocationChange, primaryButtonProps, secondaryButtonProps } = this.props;

        if (primaryButtonProps && !secondaryButtonProps) {
            const primaryButton = document.getElementsByClassName("bolt-bubble-primary-button")[0];
            if (primaryButton) {
                (primaryButton as HTMLElement).focus();
            }
        } else if (!primaryButtonProps) {
            const closeButton = document.getElementsByClassName("bolt-bubble-close")[0];
            if (closeButton) {
                (closeButton as HTMLElement).focus();
            }
        }
        onLocationChange && onLocationChange();
    };

    public dismiss = () => {
        this.customBubble.current && this.customBubble.current.dismiss();
    };
}
